import React, { useState, useEffect } from 'react';
import api from '../api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { 
  ShoppingCart, 
  Plus, 
  Trash2, 
  Pencil,
  Check,
  X,
  Lock,
  Unlock,
  Calendar,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

const ShoppingLists = () => {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedList, setSelectedList] = useState(null);
  const [expandedLists, setExpandedLists] = useState({});
  const [newList, setNewList] = useState({
    name: '',
    date: new Date().toISOString().split('T')[0]
  });
  const [newItemName, setNewItemName] = useState('');
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeListForInput, setActiveListForInput] = useState(null);

  useEffect(() => {
    fetchLists();
    fetchProductSuggestions();
  }, []);

  const fetchProductSuggestions = async () => {
    try {
      const response = await api.get('/shopping-lists/products/suggestions');
      setProductSuggestions(response.data);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
    }
  };

  const fetchLists = async () => {
    try {
      const response = await api.get('/shopping-lists');
      setLists(response.data);
      // Auto-expand open lists
      const expanded = {};
      response.data.forEach(list => {
        if (list.status === 'open') {
          expanded[list.id] = true;
        }
      });
      setExpandedLists(expanded);
    } catch (error) {
      toast.error('Errore nel caricamento delle liste');
    } finally {
      setLoading(false);
    }
  };

  const handleAddList = async () => {
    if (!newList.name.trim()) {
      toast.error('Inserisci il nome della lista');
      return;
    }

    try {
      await api.post('/shopping-lists', {
        name: newList.name,
        date: new Date(newList.date).toISOString()
      });
      toast.success('Lista creata con successo');
      setAddDialogOpen(false);
      setNewList({ name: '', date: new Date().toISOString().split('T')[0] });
      fetchLists();
    } catch (error) {
      toast.error('Errore nella creazione della lista');
    }
  };

  const handleEditList = async () => {
    if (!selectedList) return;

    try {
      await api.put(`/shopping-lists/${selectedList.id}`, {
        name: selectedList.name,
        date: new Date(selectedList.date).toISOString()
      });
      toast.success('Lista modificata');
      setEditDialogOpen(false);
      fetchLists();
    } catch (error) {
      toast.error('Errore nella modifica');
    }
  };

  const handleDeleteList = async () => {
    if (!selectedList) return;

    try {
      await api.delete(`/shopping-lists/${selectedList.id}`);
      toast.success('Lista eliminata');
      setDeleteDialogOpen(false);
      setSelectedList(null);
      fetchLists();
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleToggleStatus = async (list) => {
    const newStatus = list.status === 'open' ? 'closed' : 'open';
    try {
      await api.put(`/shopping-lists/${list.id}`, { status: newStatus });
      toast.success(newStatus === 'closed' ? 'Lista chiusa' : 'Lista riaperta');
      fetchLists();
    } catch (error) {
      toast.error('Errore nel cambio stato');
    }
  };

  const handleAddItem = async (listId) => {
    if (!newItemName.trim()) return;

    try {
      await api.post(`/shopping-lists/${listId}/items`, { name: newItemName });
      // Add to suggestions if new
      if (!productSuggestions.includes(newItemName.trim())) {
        setProductSuggestions(prev => [...prev, newItemName.trim()].sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())));
      }
      setNewItemName('');
      setShowSuggestions(false);
      fetchLists();
    } catch (error) {
      toast.error('Errore nell\'aggiunta del prodotto');
    }
  };

  const handleInputChange = (value, listId) => {
    setNewItemName(value);
    setActiveListForInput(listId);
    
    if (value.trim().length > 0) {
      const filtered = productSuggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(value.toLowerCase())
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setShowSuggestions(false);
    }
  };

  const selectSuggestion = (suggestion, listId) => {
    setNewItemName(suggestion);
    setShowSuggestions(false);
    // Focus back on input would be nice but we'll just set the value
  };

  const handleRemoveItem = async (listId, itemId) => {
    try {
      await api.delete(`/shopping-lists/${listId}/items/${itemId}`);
      // Update local state without refetching
      setLists(prevLists => prevLists.map(list => {
        if (list.id === listId) {
          return {
            ...list,
            items: list.items.filter(item => item.id !== itemId)
          };
        }
        return list;
      }));
    } catch (error) {
      toast.error('Errore nella rimozione');
    }
  };

  const handleToggleItem = async (listId, itemId) => {
    // Update local state immediately for responsive UI
    setLists(prevLists => prevLists.map(list => {
      if (list.id === listId) {
        return {
          ...list,
          items: list.items.map(item => {
            if (item.id === itemId) {
              return { ...item, checked: !item.checked };
            }
            return item;
          })
        };
      }
      return list;
    }));

    try {
      await api.put(`/shopping-lists/${listId}/items/${itemId}/toggle`);
    } catch (error) {
      toast.error('Errore nell\'aggiornamento');
      // Revert on error
      fetchLists();
    }
  };

  const toggleExpanded = (listId) => {
    setExpandedLists(prev => ({
      ...prev,
      [listId]: !prev[listId]
    }));
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D94E1F]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
            Lista della Spesa
          </h1>
          <p className="text-slate-500 mt-1">Gestisci le tue liste della spesa</p>
        </div>
        <Button
          onClick={() => {
            setNewList({ name: '', date: new Date().toISOString().split('T')[0] });
            setAddDialogOpen(true);
          }}
          className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nuova Lista
        </Button>
      </div>

      {/* Lists */}
      {lists.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-100 p-12 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto text-slate-300 mb-4" />
          <h3 className="text-lg font-medium text-slate-900 mb-2">Nessuna lista</h3>
          <p className="text-slate-500 mb-4">Crea la tua prima lista della spesa</p>
          <Button
            onClick={() => setAddDialogOpen(true)}
            className="bg-[#D94E1F] hover:bg-[#B83E15] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crea Lista
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {lists.map((list) => (
            <div 
              key={list.id} 
              className={`bg-white rounded-xl border overflow-hidden transition-all ${
                list.status === 'closed' ? 'border-slate-200 opacity-75' : 'border-[#D94E1F]/30'
              }`}
            >
              {/* List Header */}
              <div 
                className={`p-4 flex items-center justify-between cursor-pointer ${
                  list.status === 'closed' ? 'bg-slate-50' : 'bg-[#D94E1F]/5'
                }`}
                onClick={() => toggleExpanded(list.id)}
              >
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${list.status === 'closed' ? 'bg-slate-200' : 'bg-[#D94E1F]/10'}`}>
                    <ShoppingCart className={`w-5 h-5 ${list.status === 'closed' ? 'text-slate-500' : 'text-[#D94E1F]'}`} />
                  </div>
                  <div>
                    <h3 className={`font-semibold ${list.status === 'closed' ? 'text-slate-500' : 'text-slate-900'}`}>
                      {list.name}
                      {list.status === 'closed' && (
                        <span className="ml-2 text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded">
                          Chiusa
                        </span>
                      )}
                    </h3>
                    <p className="text-sm text-slate-500 flex items-center gap-1">
                      <Calendar className="w-3 h-3" />
                      {formatDate(list.date)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">
                    {list.items?.length || 0} prodotti
                  </span>
                  {expandedLists[list.id] ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* List Content */}
              {expandedLists[list.id] && (
                <div className="p-4 border-t border-slate-100">
                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(list);
                      }}
                      className={list.status === 'closed' ? 'text-green-600 border-green-200' : 'text-amber-600 border-amber-200'}
                    >
                      {list.status === 'closed' ? (
                        <>
                          <Unlock className="w-4 h-4 mr-1" />
                          Riapri
                        </>
                      ) : (
                        <>
                          <Lock className="w-4 h-4 mr-1" />
                          Chiudi
                        </>
                      )}
                    </Button>
                    {list.status === 'open' && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedList({...list, date: list.date.split('T')[0]});
                          setEditDialogOpen(true);
                        }}
                        className="text-blue-600 border-blue-200"
                      >
                        <Pencil className="w-4 h-4 mr-1" />
                        Modifica
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedList(list);
                        setDeleteDialogOpen(true);
                      }}
                      className="text-red-600 border-red-200"
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Elimina
                    </Button>
                  </div>

                  {/* Add Item (only if open) */}
                  {list.status === 'open' && (
                    <div className="relative mb-4">
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            placeholder="Aggiungi prodotto..."
                            value={activeListForInput === list.id ? newItemName : ''}
                            onChange={(e) => handleInputChange(e.target.value, list.id)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                handleAddItem(list.id);
                              }
                            }}
                            onFocus={() => {
                              setActiveListForInput(list.id);
                              if (newItemName.trim().length > 0) {
                                const filtered = productSuggestions.filter(s =>
                                  s.toLowerCase().includes(newItemName.toLowerCase())
                                );
                                setFilteredSuggestions(filtered);
                                setShowSuggestions(filtered.length > 0);
                              }
                            }}
                            onBlur={() => {
                              // Delay hiding to allow click on suggestion
                              setTimeout(() => setShowSuggestions(false), 200);
                            }}
                            className="h-10"
                            autoComplete="off"
                          />
                          {/* Suggestions Dropdown */}
                          {showSuggestions && activeListForInput === list.id && filteredSuggestions.length > 0 && (
                            <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                              {filteredSuggestions.map((suggestion, index) => (
                                <button
                                  key={index}
                                  type="button"
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-slate-100 focus:bg-slate-100 border-b border-slate-50 last:border-b-0"
                                  onMouseDown={(e) => {
                                    e.preventDefault();
                                    selectSuggestion(suggestion, list.id);
                                  }}
                                >
                                  {suggestion}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <Button
                          onClick={() => handleAddItem(list.id)}
                          className="bg-[#D94E1F] hover:bg-[#B83E15] text-white h-10"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Items List */}
                  {list.items && list.items.length > 0 ? (
                    <div className="space-y-2">
                      {list.items.map((item) => (
                        <div 
                          key={item.id}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-all ${
                            item.checked ? 'bg-slate-50' : 'bg-white border border-slate-100'
                          }`}
                        >
                          <button
                            onClick={() => list.status === 'closed' && handleToggleItem(list.id, item.id)}
                            disabled={list.status === 'open'}
                            className={`w-6 h-6 rounded border-2 flex items-center justify-center transition-all ${
                              item.checked 
                                ? 'bg-green-500 border-green-500 text-white' 
                                : list.status === 'closed'
                                  ? 'border-slate-300 hover:border-green-400 cursor-pointer'
                                  : 'border-slate-200 cursor-not-allowed'
                            }`}
                          >
                            {item.checked && <Check className="w-4 h-4" />}
                          </button>
                          <span className={`flex-1 ${
                            item.checked 
                              ? 'line-through text-slate-400' 
                              : 'text-slate-700'
                          }`}>
                            {item.name}
                          </span>
                          {list.status === 'open' && (
                            <button
                              onClick={() => handleRemoveItem(list.id, item.id)}
                              className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-center text-slate-400 py-4">
                      Nessun prodotto nella lista
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Add List Dialog */}
      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-[#D94E1F]" />
              Nuova Lista della Spesa
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-700">Nome Lista *</Label>
              <Input
                value={newList.name}
                onChange={(e) => setNewList({ ...newList, name: e.target.value })}
                placeholder="es. Spesa settimanale..."
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>

            <div>
              <Label className="text-slate-700">Data</Label>
              <Input
                type="date"
                value={newList.date}
                onChange={(e) => setNewList({ ...newList, date: e.target.value })}
                className="mt-1.5 h-12 rounded-xl"
              />
            </div>
          </div>

          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleAddList}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
            >
              Crea Lista
            </Button>
            <Button
              variant="outline"
              onClick={() => setAddDialogOpen(false)}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit List Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-slate-900 flex items-center gap-2">
              <Pencil className="w-5 h-5 text-[#D94E1F]" />
              Modifica Lista
            </DialogTitle>
          </DialogHeader>
          
          {selectedList && (
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-slate-700">Nome Lista *</Label>
                <Input
                  value={selectedList.name}
                  onChange={(e) => setSelectedList({ ...selectedList, name: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>

              <div>
                <Label className="text-slate-700">Data</Label>
                <Input
                  type="date"
                  value={selectedList.date}
                  onChange={(e) => setSelectedList({ ...selectedList, date: e.target.value })}
                  className="mt-1.5 h-12 rounded-xl"
                />
              </div>
            </div>
          )}

          <DialogFooter className="flex flex-col gap-2">
            <Button
              onClick={handleEditList}
              className="w-full h-12 bg-[#D94E1F] hover:bg-[#B83E15] text-white rounded-xl"
            >
              Salva Modifiche
            </Button>
            <Button
              variant="outline"
              onClick={() => setEditDialogOpen(false)}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Elimina Lista</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-500">
              Sei sicuro di voler eliminare la lista "{selectedList?.name}"? 
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex flex-col gap-2 sm:flex-col">
            <Button
              onClick={handleDeleteList}
              className="w-full h-12 bg-red-600 hover:bg-red-700 text-white rounded-xl"
            >
              Elimina
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              className="w-full h-12 rounded-xl"
            >
              Annulla
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ShoppingLists;
